import React, { useState, useEffect, useCallback } from 'react';
import './SavedFilters.css';
import FilterCard from './FilterCard';
import RenameFilterModal from './RenameFilterModal';
import {
  fetchSavedFilters,
  updateSavedFilter,
  deleteSavedFilter,
} from '../../services/savedFiltersApi';

// ─── WHERE → conditions reverse-parser ───────────────────────────────────────
// Handles multiple value notations for saved-filter values:
//   full operator  : { field: { $op: val } }   ← what our builder produces
//   shorthand eq   : { field: "value" }         ← common in hand-authored filters
//   shorthand in   : { field: ["a","b"] }
//   implicit AND   : { field1: val1, field2: val2 }
//   logical ops    : { $and: [...] } / { $or: [...] }

function clauseToCondition(clause, fields) {
  const entries = Object.entries(clause);
  if (entries.length !== 1) return null;

  const [fieldKey, opObj] = entries[0];
  const field = fields.find((f) => f.key === fieldKey);
  // Unknown fields are treated as 'string' so the condition still round-trips
  const fieldType = field ? field.type : 'string';

  // Shorthand array: { field: ["a", "b"] } → $in
  if (Array.isArray(opObj)) {
    return { field: fieldKey, fieldType, operator: '$in', value: opObj };
  }

  // Shorthand equality: { field: primitiveValue } → $eq
  if (typeof opObj !== 'object' || opObj === null) {
    return {
      field: fieldKey,
      fieldType,
      operator: '$eq',
      value: opObj === null ? '' : opObj,
    };
  }

  // Full operator notation: { field: { $op: val } }
  const opEntries = Object.entries(opObj);
  if (opEntries.length !== 1) return null;

  const [operator, value] = opEntries[0];
  return {
    field: fieldKey,
    fieldType,
    operator,
    value: value === null ? '' : value,
  };
}

export function whereToConditions(whereValue, fields) {
  if (typeof whereValue === 'string') {
    try {
      whereValue = JSON.parse(whereValue);
    } catch (_) {}
  }
  if (
    !whereValue ||
    typeof whereValue !== 'object' ||
    Array.isArray(whereValue)
  ) {
    return { conditions: [], logicalOp: '$and' };
  }

  const entries = Object.entries(whereValue);
  if (entries.length === 0) return { conditions: [], logicalOp: '$and' };

  // Logical operator wrapper: { $and: [...] } or { $or: [...] }
  if (
    entries.length === 1 &&
    (entries[0][0] === '$and' || entries[0][0] === '$or')
  ) {
    const [logicalOp, parts] = entries[0];
    if (!Array.isArray(parts)) return { conditions: [], logicalOp };
    const conditions = parts
      .map((p) => clauseToCondition(p, fields))
      .filter(Boolean)
      .map((c, i) => ({ ...c, id: Date.now() + i }));
    return { conditions, logicalOp };
  }

  // Multiple top-level keys with no logical wrapper: { f1: v1, f2: v2 } → implicit AND
  if (entries.length > 1) {
    const conditions = entries
      .map(([k, v]) => clauseToCondition({ [k]: v }, fields))
      .filter(Boolean)
      .map((c, i) => ({ ...c, id: Date.now() + i }));
    return { conditions, logicalOp: '$and' };
  }

  // Single condition
  const cond = clauseToCondition(whereValue, fields);
  return {
    conditions: cond ? [{ ...cond, id: Date.now() }] : [],
    logicalOp: '$and',
  };
}

// ─── FilterPanel ──────────────────────────────────────────────────────────────

export default function FilterPanel({
  resource,
  fields,
  activeSavedFilter,
  onApplySavedFilter,
  onSavedFilterUpdated,
  onDeletedSavedFilter,
  onClose,
  inline,
}) {
  // ── Saved filters state ────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('standard');
  const [standardFilters, setStandardFilters] = useState([]);
  const [customFilters, setCustomFilters] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [filtersError, setFiltersError] = useState(null);

  // ── Rename modal state ─────────────────────────────────────────────────────
  const [renamingFilter, setRenamingFilter] = useState(null);
  const [renamingLoading, setRenamingLoading] = useState(false);

  // ── Load saved filters once on panel open ──────────────────────────────────
  useEffect(() => {
    setLoadingFilters(true);
    setFiltersError(null);
    fetchSavedFilters(resource)
      .then(({ data }) => {
        setStandardFilters((data && data.standard) || []);
        setCustomFilters((data && data.custom) || []);
        setLoadingFilters(false);
      })
      .catch((err) => {
        setFiltersError((err && err.message) || 'Failed to load saved filters');
        setLoadingFilters(false);
      });
  }, [resource]);

  const refreshFilters = useCallback(() => {
    return fetchSavedFilters(resource)
      .then(({ data }) => {
        setStandardFilters((data && data.standard) || []);
        setCustomFilters((data && data.custom) || []);
      })
      .catch(() => {});
  }, [resource]);

  // ── Delete filter (optimistic) ─────────────────────────────────────────────
  const handleDeleteFilter = useCallback(
    (filterId) => {
      setCustomFilters((prev) => prev.filter((f) => f.id !== filterId));
      deleteSavedFilter(resource, filterId)
        .then(() => {
          onDeletedSavedFilter(filterId);
        })
        .catch(() => {
          // Rollback: refetch the real list
          refreshFilters();
        });
    },
    [resource, refreshFilters, onDeletedSavedFilter]
  );

  // ── Rename filter ──────────────────────────────────────────────────────────
  const handleRenameFilter = useCallback(
    ({ label, description }) => {
      if (!renamingFilter) return;
      setRenamingLoading(true);
      updateSavedFilter(resource, renamingFilter.id, { label, description })
        .then(() => {
          setRenamingLoading(false);
          const updated = { ...renamingFilter, label, description };
          setRenamingFilter(null);
          refreshFilters();
          if (activeSavedFilter && activeSavedFilter.id === renamingFilter.id) {
            onSavedFilterUpdated(updated);
          }
        })
        .catch(() => {
          setRenamingLoading(false);
        });
    },
    [
      renamingFilter,
      resource,
      refreshFilters,
      activeSavedFilter,
      onSavedFilterUpdated,
    ]
  );

  // ── Tab content renderer ───────────────────────────────────────────────────
  const renderTabContent = () => {
    if (loadingFilters) {
      return (
        <div className="sfp-filter-list">
          {[1, 2].map((i) => (
            <div key={i} className="sfp-skeleton-card">
              <div className="sfp-skeleton-line sfp-skeleton-line--title" />
              <div className="sfp-skeleton-line sfp-skeleton-line--desc" />
              <div className="sfp-skeleton-line sfp-skeleton-line--preview" />
            </div>
          ))}
        </div>
      );
    }

    if (filtersError) {
      return (
        <div className="sfp-inline-error">
          <span>⚠ {filtersError}</span>
          <button className="sfp-retry-btn" onClick={refreshFilters}>
            Retry
          </button>
        </div>
      );
    }

    if (activeTab === 'standard') {
      if (standardFilters.length === 0) {
        return (
          <div className="sfp-empty">
            No standard filters defined for this resource yet.
          </div>
        );
      }
      return (
        <div className="sfp-filter-list">
          {standardFilters.map((f) => (
            <FilterCard
              key={f.id}
              filter={f}
              isActive={!!(activeSavedFilter && activeSavedFilter.id === f.id)}
              onUse={onApplySavedFilter}
              onRename={() => {}}
              onEdit={() => {}}
              onDelete={() => {}}
            />
          ))}
        </div>
      );
    }

    // My Filters tab
    if (customFilters.length === 0) {
      return (
        <div className="sfp-empty sfp-empty--illustrated">
          <div className="sfp-empty-icon">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#d0d0d0"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
          </div>
          <p className="sfp-empty-title">No saved filters yet</p>
          <p className="sfp-empty-desc">
            You haven't saved any filters yet. Build a filter above and save it
            for later.
          </p>
        </div>
      );
    }

    return (
      <div className="sfp-filter-list">
        {customFilters.map((f) => (
          <FilterCard
            key={f.id}
            filter={f}
            isActive={!!(activeSavedFilter && activeSavedFilter.id === f.id)}
            onUse={onApplySavedFilter}
            onRename={setRenamingFilter}
            onEdit={() => {}}
            onDelete={handleDeleteFilter}
          />
        ))}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className={'sfp-panel' + (inline ? ' sfp-panel--inline' : '')}
      role={inline ? undefined : 'dialog'}
      aria-modal={inline ? undefined : 'true'}
      aria-label={inline ? undefined : 'Filter panel'}
    >
      <div className="sfp-panel-header">
        <span className="sfp-panel-title">Saved Filters</span>
        {onClose && (
          <button
            className="sfp-close-btn"
            onClick={onClose}
            aria-label="Close filter panel"
          >
            ✕
          </button>
        )}
      </div>

      {/* ── Section A: Saved Filters ──────────────────────────────────────── */}
      <div className="sfp-section">
        <div className="sfp-section-label">Saved Filters</div>
        <div className="sfp-tabs" role="tablist">
          <button
            className={`sfp-tab${
              activeTab === 'standard' ? ' sfp-tab--active' : ''
            }`}
            role="tab"
            aria-selected={activeTab === 'standard'}
            onClick={() => setActiveTab('standard')}
          >
            Standard
          </button>
          <button
            className={`sfp-tab${activeTab === 'my' ? ' sfp-tab--active' : ''}`}
            role="tab"
            aria-selected={activeTab === 'my'}
            onClick={() => setActiveTab('my')}
          >
            My Filters
            {customFilters.length > 0 && (
              <span className="sfp-tab-count">{customFilters.length}</span>
            )}
          </button>
        </div>
        <div className="sfp-tab-content" role="tabpanel">
          {renderTabContent()}
        </div>
      </div>

      {/* ── Rename modal ──────────────────────────────────────────────────── */}
      <RenameFilterModal
        filter={renamingFilter}
        isOpen={!!renamingFilter}
        onSave={handleRenameFilter}
        onClose={() => setRenamingFilter(null)}
        loading={renamingLoading}
      />
    </div>
  );
}
